import DeleteFileModal from './modal/delete_file';
import { dragFileToFolderThunk } from '../../redux/thunks/file';
import { FileObject } from '../../params/redux/file';
import { formatTimeUptoNow } from '../../helper';
import ImageClass from '../../assets';
import { MoreVert } from '@mui/icons-material';
import { settingSelectedFile } from '../../redux/slices/file';
import { useParams } from 'react-router';
import { AppDispatch, RootState } from '../../redux/store';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { brand, gray } from '../../theme/themePrimitives';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';

interface FileItemProps {
    file: FileObject;
    isAssigned?: boolean;
}

const FileItemComponent = ({ file, isAssigned = false }: FileItemProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [deleteFile, setDeleteFile] = useState<boolean>(false);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const { selectedFolder } = useSelector((state: RootState) => state.folder);
    const draggingRef = useRef(false);
    const open = Boolean(anchorEl);

    const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAnchorEl(null);
    };

    const handleFileView = () => {
        if (!deleteFile) {
            window.open(`${process.env.REACT_APP_FILE_PATH}${file.file_path}`, '_blank');
        }
    };

    const handleEditFile = (event: React.MouseEvent<HTMLLIElement> | React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        dispatch(settingSelectedFile(file));
    };

    const handleDeleteFile = (event: React.MouseEvent<HTMLLIElement>) => {
        event.stopPropagation();
        setDeleteFile(true);
        setAnchorEl(null);
    };

    const handleCloseModal = (event?: React.MouseEvent<HTMLButtonElement>) => {
        if (event) event.stopPropagation();
        setDeleteFile(false);
    };

    useEffect(() => {
        const handleDragEndGlobal = () => {
            draggingRef.current = false;
            setIsDragging(false);
        };
        // window.addEventListener('dragleave', handleDragEndGlobal);
        window.addEventListener('dragend', handleDragEndGlobal);
        window.addEventListener('drop', handleDragEndGlobal);
        // window.addEventListener('mouseup', handleDragEndGlobal);

        return () => {
            window.removeEventListener('dragend', handleDragEndGlobal);
            // window.addEventListener('dragleave', handleDragEndGlobal);
            window.removeEventListener('drop', handleDragEndGlobal);
            // window.removeEventListener('mouseup', handleDragEndGlobal);
        };
    }, []);

    const moveToMainHandler = async (event: React.MouseEvent<HTMLLIElement>) => {
        if (event) event.stopPropagation();
        try {
            await dispatch(dragFileToFolderThunk({
                fileId: file.id,
                folderId: 0,
                client_id: parseInt(id as string, 10)
            })).unwrap();
        } catch (e) {
            //
        }
    };

    return (
        <Box
            draggable
            onDragStart={(e) => {
                e.dataTransfer.setData('fileId', file.id.toString());
                draggingRef.current = true;
                requestAnimationFrame(() => {
                    if (draggingRef.current) {
                        setIsDragging(true);
                    }
                });
            }}
            onDragEnd={() => {
                draggingRef.current = false;
                setIsDragging(false);
            }}
            height="100%"
            minWidth="250px"
            maxWidth="250px"
            border={isDragging ? `2px dashed ${brand[300]}` : `1px solid ${gray[200]}`}
            borderRadius="10px"
            sx={{
                'transform': isDragging ? 'scale(1.02)' : 'scale(1.0)',
                'transition': 'all 0.2s ease',
                'opacity': isDragging ? 0.7 : 1,
                'backgroundColor': isDragging ? brand[100] : '',
                'cursor': isDragging ? 'grabbing' : 'grab',
                '&:hover': {
                    backgroundColor: brand[100],
                    cursor: 'pointer'
                }
            }}
            onClick={(e) => (file.file_type === 'internal' ? handleEditFile(e) : handleFileView())}
        >
            <Box
                maxHeight="18vh"
                minHeight="18vh"
                bgcolor={gray[50]}
                display="flex"
                justifyContent="center"
                alignItems="center"
                px={2}
                sx={{
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px'
                }}>
                <Box
                    sx={{
                        backgroundImage: `url(${['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'tiff'].includes(file.extension)
                            ? `${process.env.REACT_APP_FILE_PATH}${file.file_path}`
                            : ImageClass.document})`,
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                        pointerEvents: 'none',
                        width: '100%',
                        height: '14vh',
                        backgroundRepeat: 'no-repeat'
                    }}
                >
                </Box>
            </Box>
            <Box minHeight="8vh" maxHeight="8vh" paddingLeft={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" maxWidth="200px" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                        <b>{file.file_name}</b>
                    </Typography>
                    {!isAssigned && <IconButton onClick={handleButtonClick}>
                        <MoreVert fontSize="small" />
                    </IconButton>}
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleMenuClose}
                        MenuListProps={{
                            'aria-labelledby': 'add-pipeline-button'
                        }}
                    >
                        <MenuItem onClick={handleEditFile}>
                            <Box color={gray[500]} display="flex" alignItems="center" gap="10px">Edit</Box>
                        </MenuItem>
                        {file.file_type === 'internal' && <MenuItem onClick={handleFileView}>
                            <Box color={gray[500]} display="flex" alignItems="center" gap="10px">Download</Box>
                        </MenuItem>}
                        {selectedFolder && <MenuItem onClick={moveToMainHandler}>
                            <Box color={gray[500]} display="flex" alignItems="center" gap="10px">Move to main</Box>
                        </MenuItem>}
                        <MenuItem onClick={handleDeleteFile}>
                            <Box color={gray[500]} display="flex" alignItems="center" gap="10px">Delete</Box>
                        </MenuItem>
                    </Menu>
                </Box>
                <Typography variant="body2" color="textSecondary">{formatTimeUptoNow(file.created_at)}</Typography>
            </Box>
            <DeleteFileModal open={deleteFile} onClose={handleCloseModal} file={file} />
        </Box>);
};

export default FileItemComponent;